import type { ConversationCarry } from "@cleo/contracts";

/**
 * W-79778d2b: an undo on an engine that keeps every turn in its own session
 * (Claude) owes that engine a fresh start with the kept messages. A fork whose
 * carry is still pending already starts fresh with only what it holds. A
 * pending engine switch keeps its target and also owes the undo, so a Send
 * that cancels the switch still starts the bound engine fresh.
 */
export function carryAfterUndo(existing: ConversationCarry | null): ConversationCarry {
  if (existing?.state === "pending" && existing.reason === "fork") return existing;
  if (existing?.state === "pending") return { ...existing, afterUndo: true };
  return { state: "pending", reason: "engine-switch", handoffId: null, afterUndo: true };
}

/**
 * A delivered carry owes nothing more, so it no longer says why it was owed
 * after an undo. The server's stores keep no such flag: their reads tell it
 * from the event log, and only while the carry is pending.
 */
export function deliveredCarry(existing: ConversationCarry, handoffId: string): ConversationCarry {
  return { state: "delivered", reason: existing.reason, handoffId };
}

/**
 * Can 21: a Send naming the bound engine again, before the engine switched to
 * ran, drops the switch's carry. A carry an undo also owes stays: the bound
 * engine still holds the undone turns. So does a fork's (a switch keeps it, and
 * a fork's first engine is still owed what the fork holds).
 */
export function carryAfterSwitchReverted(
  existing: ConversationCarry | null,
): ConversationCarry | null {
  return existing?.state === "pending" &&
    (existing.afterUndo === true || existing.reason === "fork")
    ? existing
    : null;
}
